import { GenericServiceBase } from '../generic-service';

/**
 * Natureza da linha. `MANUAL` é a anotação escrita por um atendente; os demais
 * são eventos gravados pelo servidor — imutáveis, e com autor nulo quando
 * nenhum humano os provocou.
 */
export type TipoAnotacaoInterna =
    | 'MANUAL'
    | 'ATENDIMENTO_INICIADO'
    | 'ATENDIMENTO_FINALIZADO'
    | 'ATENDIMENTO_TRANSFERIDO'
    | 'BOT_DISPARADO'
    | 'DISPARO_ENVIADO'
    /** Ligação de voz ou vídeo, gravada no estado terminal. */
    | 'LIGACAO'
    /** Fato registrado pelo Agente de IA (bot-ia): ciclo, ferramenta, ou guarda. */
    | 'AGENTE_IA'
    /** Resposta da pergunta de conversão que o inbox comercial faz ao finalizar. */
    | 'CONVERSAO'
    /** O contato trocou o número de telefone no WhatsApp (aviso da Cloud API). */
    | 'CONTATO_ALTEROU_NUMERO'
    /** Uma execução do rodízio do canal: política, fallback e quem estava fora de turno. */
    | 'RODIZIO'
    /** Uma ação do bot falhou (template ou Lista de Canais não enviados) — em geral o bot saiu do atendimento. */
    | 'BOT_FALHOU';

export type OrigemBotDisparado = 'USUARIO' | 'MENSAGEM' | 'AGENDAMENTO' | 'BOT';

/**
 * Metadados de `AGENTE_IA`.
 *
 * `evento` é string livre, e não união fechada, de propósito: o vocabulário nasce no bot-ia
 * e cresce lá. Fechá-lo aqui faria um marcador novo do Agente aparecer como erro de tipo num
 * repositório que não o criou — e, pior, sumir da tela até alguém vir atualizar a união.
 * Evento desconhecido cai no ícone genérico do Agente.
 */
export type MetadadosAgenteIa = {
    evento: string;
    /** Frase pronta, escrita pelo bot-ia. */
    resumo?: string | null;
    nomeAgente?: string | null;
    idAgenteBotIa?: string | null;
    ferramenta?: string | null;
    nomeCanal?: string | null;
    idCanal?: string | null;
};

/** Dados estruturados do evento. Sempre nulo em `MANUAL`. */
export type MetadadosAnotacaoInterna = {
    automatico?:         boolean;
    idUsuarioDestino?:   string | null;
    nomeUsuarioDestino?: string | null;
    removeuAtendente?:   boolean;
    idCanal?:            string | null;
    /** Snapshot do nome do canal. Ausente nas linhas gravadas antes do campo. */
    nomeCanal?:          string | null;
    idBot?:              string;
    nomeBot?:            string | null;
    origem?:             OrigemBotDisparado;
    /**
     * `DISPARO_ENVIADO` — a campanha que criou ou tocou o atendimento. O tipo do
     * evento fala `DISPARO` porque é o nome interno no servidor; a interface diz
     * campanha. `nomeDisparo` é snapshot do momento do envio: renomear a campanha
     * depois não reescreve a linha, que é imutável.
     */
    idDisparo?:          string;
    nomeDisparo?:        string;
    /** Envio (agendamento) que originou o evento. É o alvo do salto no modal. */
    idAgendamento?:      string;
    /** `true` = a campanha CRIOU o atendimento; `false` = apenas o TOCOU. */
    criouAtendimento?:   boolean;
    /**
     * `LIGACAO` — a ligação de voz que terminou. Gravada só no estado terminal,
     * porque é ali que a duração existe.
     *
     * `duracaoSegundos` nulo NÃO é zero: é "não houve fala" (perdida, recusada,
     * ou encerrada antes de atender). É essa diferença que muda a frase.
     */
    idLigacao?:          string;
    direcao?:            'RECEBIDA' | 'REALIZADA';
    situacao?:           'TOCANDO' | 'ATENDIDA' | 'PERDIDA' | 'RECUSADA' | 'ENCERRADA';
    duracaoSegundos?:    number | null;
    ehVideo?:            boolean;
    /** Motivo cru do WhatsApp. Diagnóstico, não texto de tela. */
    motivo?:             string | null;
    /**
     * `CONVERSAO` — resposta da pergunta do inbox comercial. A linha só existe quando
     * o atendente REALMENTE respondeu: finalização em lote, transferir-e-finalizar e o
     * ramo "Avaliar" não exibem a pergunta e por isso não geram anotação nenhuma.
     */
    houve?:              boolean;
    /**
     * `CONTATO_ALTEROU_NUMERO` — par antigo/novo da troca notificada pela Meta.
     *
     * O cadastro NUNCA é alterado automaticamente, então a faixa é sempre um
     * pedido de ação. `perspectiva` diz de qual ponta esta conversa vê o fato:
     * `ORIGEM` é o contato que tinha o número antigo; `DESTINO` é a conversa
     * aberta sob o número novo, que na API Oficial nasce como um segundo
     * cadastro porque a Cloud API não entrega LID.
     */
    numeroAnterior?: string;
    numeroNovo?:     string;
    perspectiva?:    'ORIGEM' | 'DESTINO';
    /**
     * `RODIZIO` — uma execução do rodízio do canal. `fallback` = política por
     * horário sem ninguém em turno, caiu no rodízio normal. `indisponiveis` só
     * existe na política por horário (nulo no normal). Sorteado nulo = ficou na
     * fila do canal.
     */
    tipoRodizio?:         'NORMAL' | 'RESPEITA_JORNADA_TRABALHO';
    fallback?:            boolean;
    idUsuarioSorteado?:   string | null;
    nomeUsuarioSorteado?: string | null;
    indisponiveis?:       { idUsuario: string; nome: string; motivo: string; ate: string | null }[] | null;
    /**
     * `BOT_FALHOU` — ação em que o fluxo parou (`TipoBotAcao`), por quê (`causa`), e se
     * o bot saiu do atendimento. `motivo` é a mensagem do erro de origem, incluindo a
     * frase da Meta: é ela que responde por que o contato ficou sem resposta.
     * `causa` ausente = linha de template gravada antes do campo existir.
     */
    acao?:              string;
    nomeTemplate?:      string | null;
    causa?:             'SEM_CANAIS' | 'ENVIO_RECUSADO' | 'ENTREGA_RECUSADA' | null;
    codigoErro?:        string | null;
    fluxoInterrompido?: boolean;
} | null;

/** Espelha `ResponseAnotacaoInternaDto` do whiz-server. */
export type AnotacaoInterna = {
    id:            string;
    conteudo:      string;
    tipo:          TipoAnotacaoInterna;
    metadados:     MetadadosAnotacaoInterna;
    idInbox:       string;
    idUsuario:     string | null;
    autor:         { id: string; nome: string } | null;
    idContato:     string | null;
    idAtendimento: string | null;
    idGrupo:       string | null;
    dataCriacao:   string;
    dataEdicao:    string | null;
    editada:       boolean;
    del:           boolean;
};

/**
 * Exatamente um alvo. `idInbox`/`idContato` NÃO existem aqui de propósito: o
 * servidor os deriva do atendimento e ignora (remove) qualquer coisa a mais.
 */
export type CriarAnotacaoInternaPayload = {
    conteudo:       string;
    idAtendimento?: string;
    idGrupo?:       string;
};

/**
 * Alvo da listagem. Exatamente uma das três formas — o par `idInbox`+`idContato`
 * é o modo cross-atendimento e exige `inbox.ver_interacoes_atendimento`.
 */
export type ListarAnotacoesInternasParams = {
    idAtendimento?: string;
    idInbox?:       string;
    idContato?:     string;
    idGrupo?:       string;
    /** Teto da janela: só anotações com dataCriacao <= ate. */
    ate?:           string;
    limite?:        number;
};

export class AnotacoesInternasService extends GenericServiceBase {
    constructor() { super('anotacoes-internas'); }

    async listar(params: ListarAnotacoesInternasParams): Promise<AnotacaoInterna[]> {
        return await this.f.get('/', params) as AnotacaoInterna[];
    }

    async criar(payload: CriarAnotacaoInternaPayload): Promise<AnotacaoInterna> {
        return await this.f.post('/', payload) as AnotacaoInterna;
    }

    async editar(id: string, conteudo: string): Promise<AnotacaoInterna> {
        return await this.f.patch(`/${id}`, { conteudo }) as AnotacaoInterna;
    }

    async remover(id: string): Promise<AnotacaoInterna> {
        return await this.f.delete(`/${id}`) as AnotacaoInterna;
    }
}
